import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Router, UNSAFE_createBrowserHistory as createBrowserHistory } from 'react-router-dom';
import { prepareRoute } from '../../data/routeLoaders';
import { portalNavigate, cancelPortalNavigation, isPortalMoving, portalMotionMode } from './portalNavigate';

const controlFor = target => target?.closest?.('a,button,[role="button"],[data-portal-to]');
export function controlOrigin(event, pressed) {
  const control = controlFor(event.target);
  if (event.detail !== 0 && pressed && pressed.control === control) return pressed.origin;
  const rect = control?.getBoundingClientRect();
  return rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
}

/**
 * Own the history subscription so POP can be captured before React changes the route.
 * Uses the same history implementation as BrowserRouter (including usr/key/idx state).
 * A click intent lasts only through that synchronous event dispatch. Effects, redirects,
 * and async save completions cannot inherit a previous pointer event.
 */
export default function PortalRouter({ children, initialTransition = true }) {
  const historyRef = useRef(null);
  if (!historyRef.current) historyRef.current = createBrowserHistory({ v5Compat: true });
  const history = historyRef.current;
  const [state, setState] = useState({ action: history.action, location: history.location });
  const shown = useRef(state);
  const committing = useRef(false);
  const intent = useRef(null);
  const pressed = useRef(null);
  const speculative = useRef(null);
  const popOrigin = useRef(null);
  const positions = useRef(new Map());
  const scrollPath = useRef(state.location.pathname);
  // history.go() lands asynchronously. A push made before it lands gets popped by it — a
  // viewer closed with Back and a ring tapped straight after opened and vanished. Until the
  // pending POP arrives, pushes and replaces wait their turn, then run in order.
  const pendingPop = useRef(false);
  const queued = useRef([]);
  const flushRef = useRef(() => {});

  // Direct links and refreshes enter through the same ring, without replaying an old page.
  useEffect(() => {
    if (!initialTransition) return undefined;
    let disposed = false;
    queueMicrotask(() => {
      if (!disposed && !isPortalMoving()) portalNavigate({ mode: 'entry', commit: () => {} });
    });
    return () => { disposed = true; };
  }, [initialTransition]);

  useLayoutEffect(() => {
    const restoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    const unlisten = history.listen(next => {
      if (next.action === 'POP' && pendingPop.current) {
        pendingPop.current = false;
        Promise.resolve().then(() => flushRef.current());
      }
      const origin = popOrigin.current;
      popOrigin.current = null;
      if (committing.current || next.action !== 'POP' || shown.current.location.pathname === next.location.pathname || document.hidden) {
        shown.current = next; setState(next); return;
      }
      cancelPortalNavigation();
      // Cancelled jobs release their lock in a microtask before this reverse starts.
      Promise.resolve().then(() => portalNavigate({
        back: true, origin,
        commit: () => {
          shown.current = next; setState(next);
        },
      }));
    });
    const save = () => positions.current.set(shown.current.location.key, { x: window.scrollX, y: window.scrollY });
    window.addEventListener('scroll', save, { passive: true });
    return () => { unlisten(); window.removeEventListener('scroll', save); window.history.scrollRestoration = restoration; cancelPortalNavigation(); };
  }, [history]);

  // A tap while a Portal is moving. During a view transition the browser paints the animation
  // over the page, so the tap lands on <html> — outside React, on nothing — and used to vanish:
  // the rail looked ready on first load but ignored every tap until the arrival had finished.
  //   entry         cosmetic: the tap ends it on the spot and goes where it was aimed
  //   viewer-close  the page under the tap is the one it's going back to: deliver it after
  //   page changes  dropped — replaying a tap on the page you're leaving would hit the wrong thing
  useEffect(() => {
    let pending = null;
    const deliver = (x, y) => {
      const control = controlFor(document.elementFromPoint(x, y));
      if (control && control.isConnected && !control.disabled) control.click();
    };
    const onClick = (e) => {
      if (!isPortalMoving() || e.button !== 0) return;
      const mode = portalMotionMode();
      if (mode !== 'entry' && mode !== 'viewer-close') return;
      e.preventDefault(); e.stopPropagation();
      const x = e.clientX, y = e.clientY;
      const tap = { x, y };
      pending = tap;
      if (mode === 'entry') cancelPortalNavigation();
      const started = Date.now();
      const tick = () => {
        if (pending !== tap) return;                      // a newer tap replaced it
        if ((isPortalMoving() || pendingPop.current) && Date.now() - started < 2000) { setTimeout(tick, 40); return; }
        pending = null;
        if (!isPortalMoving()) deliver(x, y);
      };
      setTimeout(tick, 0);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  useLayoutEffect(() => {
    if (scrollPath.current === state.location.pathname) return;
    scrollPath.current = state.location.pathname;
    if (state.action === 'POP') {
      const pos = positions.current.get(state.location.key);
      if (pos) window.scrollTo(pos.x, pos.y);
    } else if (!state.location.hash) window.scrollTo(0, 0);
  }, [state.location.pathname, state.location.key, state.location.hash, state.action]);

  const navigator = useMemo(() => {
    const move = (method, to, userState, options) => {
      if (pendingPop.current) { queued.current.push([method, to, userState, options]); return; }
      const tap = intent.current;
      intent.current = null;
      const warm = speculative.current;
      speculative.current = null;
      const target = typeof to === 'string' ? new URL(to, window.location.href).pathname : to.pathname;
      // Query-only history belongs to sheets/viewers and keeps the page mounted.
      // Redirects during an existing snapshot belong to that same journey. Every other
      // page change animates, including programmatic navigation without a pointer.
      if (target === shown.current.location.pathname || (method === 'replace' && isPortalMoving())) {
        history[method](to, userState, options); return;
      }
      if (isPortalMoving()) return;
      const path = typeof to === 'string' ? to : `${to.pathname}${to.search || ''}${to.hash || ''}`;
      positions.current.set(shown.current.location.key, { x: window.scrollX, y: window.scrollY });
      portalNavigate({
        origin: tap?.origin,
        load: () => warm?.path === path ? warm.promise : prepareRoute(path),
        commit: resolved => {
          committing.current = true;
          try { history[method](resolved || to, userState, options); } finally { committing.current = false; }
        },
      });
    };
    flushRef.current = () => {
      const ops = queued.current;
      queued.current = [];
      ops.forEach(op => move(...op));
    };
    return {
      createHref: history.createHref, encodeLocation: history.encodeLocation,
      go: delta => {
        const run = () => {
          popOrigin.current = intent.current?.origin;
          intent.current = null;
          pendingPop.current = true;
          history.go(delta);
          // A go() the browser ignores (nothing to go back to) never fires a POP.
          setTimeout(() => { if (pendingPop.current) { pendingPop.current = false; flushRef.current(); } }, 1000);
        };
        if (!isPortalMoving()) { run(); return; }
        // Mid-motion, Back waits for the motion rather than vanishing. It used to be dropped:
        // a viewer that couldn't open closed itself with Back, the Back was ignored, and the
        // page kept ?reel= with nothing on screen.
        const started = Date.now();
        const wait = () => {
          if (isPortalMoving() && Date.now() - started < 2500) { setTimeout(wait, 40); return; }
          run();
        };
        setTimeout(wait, 40);
      },
      push: (...args) => move('push', ...args), replace: (...args) => move('replace', ...args),
    };
  }, [history]);

  const onPointerDownCapture = e => {
    speculative.current = null;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
    const control = controlFor(e.target);
    pressed.current = { control, origin: { x: e.clientX, y: e.clientY } };
    const path = control?.getAttribute('data-portal-to') || control?.getAttribute('href');
    if (path?.startsWith('/') && !path.startsWith('//')) {
      const promise = prepareRoute(path);
      promise.catch(() => {});
      speculative.current = { path, promise };
    }
  };
  const onClickCapture = e => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
    // Mid-motion taps are handled (or deliberately dropped) by the document-level listener.
    if (isPortalMoving()) { e.preventDefault(); e.stopPropagation(); return; }
    const tap = { origin: controlOrigin(e, pressed.current) };
    pressed.current = null;
    intent.current = tap;
    // Native browsers can drain microtasks BETWEEN React's capture and bubble listeners.
    // A microtask here erased real clicks before navigate() ran (jsdom doesn't reproduce it).
    // Clear on our bubble handler instead; the timer only covers stopped propagation.
    setTimeout(() => { if (intent.current === tap) intent.current = null; }, 0);
  };
  return (
    <Router location={state.location} navigationType={state.action} navigator={navigator}>
      <div style={{ display: 'contents' }} onPointerDownCapture={onPointerDownCapture}
        onPointerCancelCapture={() => { pressed.current = null; speculative.current = null; }} onClickCapture={onClickCapture}
        onClick={() => { intent.current = null; speculative.current = null; }}>
        {children}
      </div>
    </Router>
  );
}
